// Stok Listesi Raporu — DataTable + filtreler + Yazdır/Excel/PDF + şablon seçimi.
// Şablon altyapısı (FastReport benzeri) basit: config_json içerisinde kolon listesi + başlık + logo.
// İleride LabelDesigner gibi bir tasarımcı eklenecek; şu an "Standart" şablon + kullanıcı kayıtlı şablonlar.
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import DataTable from '../../components/DataTable';
import { Button, FilterBar, ListPageHeader, Modal } from '../../components/ui';
import { renderPrintHtml as renderBandedHtml } from './ReportDesignerPage';

const fmtQty = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtPercent = (n) => (n == null) ? '' : (parseFloat(n).toFixed(0) + '%');

// Tasarımcının düzenlediği tüm kolonların metadata'sı
const ALL_COLUMNS = [
  { key: 'code',     label: 'Stok Kodu',  default: true },
  { key: 'name',     label: 'Stok Adı',   default: true },
  { key: 'barcode',  label: 'Barkod',     default: true },
  { key: 'category', label: 'Kategori',   default: true },
  { key: 'brand',    label: 'Marka',      default: true },
  { key: 'unit',     label: 'Birim',      default: true },
  { key: 'vat',      label: 'KDV %',      default: true },
];

const DEFAULT_COLUMNS = ALL_COLUMNS.filter(c => c.default).map(c => c.key);

const STANDARD_TEMPLATE = {
  id: '__default__',
  name: 'Standart Şablon',
  is_default: true,
  config: {
    title: 'Stok Listesi',
    subtitle: '',
    show_logo: true,
    logo_url: '',
    columns: DEFAULT_COLUMNS,
    show_warehouse_columns: true,
    show_total_column: true,
    page_orientation: 'landscape',
    paper_size: 'A4',
    header_color: '#1e293b',
    header_text_color: '#ffffff',
    accent_color: '#3b82f6',
    font_family: 'Segoe UI, Arial, sans-serif',
    font_size: 11,
    show_filter_summary: true,
    show_row_count: true,
    show_page_number: true,
    footer_text: '',
    striped_rows: true,
  },
};

// Eski (eksik) config'leri standartla birleştir → yeni alanlar undefined kalmasın
const mergeConfig = (cfg) => ({ ...STANDARD_TEMPLATE.config, ...(cfg || {}) });

export default function StockListReportPage({ onNavigate }) {
  const [filters, setFilters] = useState({ search: '', status: 'active', category_id: '', brand_id: '', warehouse_id: '' });
  const [showFilterBar, setShowFilterBar] = useState(true);
  const [stocks, setStocks] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState('');

  const [templates, setTemplates] = useState([STANDARD_TEMPLATE]);
  const [activeTemplateId, setActiveTemplateId] = useState('__default__');
  const [showTemplateMgr, setShowTemplateMgr] = useState(false);

  const printRef = useRef(null);

  const activeTemplate = useMemo(
    () => templates.find(t => t.id === activeTemplateId) || STANDARD_TEMPLATE,
    [templates, activeTemplateId]
  );
  const tplConfig = mergeConfig(activeTemplate.config);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search)       params.search       = filters.search;
      if (filters.status)       params.status       = filters.status;
      if (filters.category_id)  params.category_id  = filters.category_id;
      if (filters.brand_id)     params.brand_id     = filters.brand_id;
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id;
      const r = await api.get('/reports/stocks/list', { params });
      if (r.data.success) {
        setStocks(r.data.data.stocks || []);
        setWarehouses(r.data.data.warehouses || []);
      }
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Filtre dropdown'ları için kategori + marka + depo
  useEffect(() => {
    (async () => {
      try {
        const [c, b, w, cmp] = await Promise.all([
          api.get('/stocks/categories').catch(() => ({ data: [] })),
          api.get('/stocks/brands').catch(() => ({ data: [] })),
          api.get('/warehouses').catch(() => ({ data: { data: [] } })),
          api.get('/auth/me').catch(() => ({ data: { user: null, company: null } })),
        ]);
        setCategories(Array.isArray(c.data) ? c.data : (c.data?.data || []));
        setBrands(Array.isArray(b.data) ? b.data : (b.data?.data || []));
        setWarehouses(prev => prev.length ? prev : (Array.isArray(w.data?.data) ? w.data.data : (Array.isArray(w.data) ? w.data : [])));
        setCompanyName(cmp.data?.company?.name || cmp.data?.company?.title || '');
      } catch (_) {}
    })();
  }, []);

  // Şablonları yükle
  const loadTemplates = useCallback(async () => {
    try {
      const r = await api.get('/reports/templates', { params: { report_key: 'stock_list' } });
      if (r.data.success) {
        const userTpls = (r.data.data || []).map(t => ({
          id: t.id, name: t.name, is_default: !!t.is_default,
          config: t.config_json ? (typeof t.config_json === 'string' ? JSON.parse(t.config_json) : t.config_json) : {},
        }));
        setTemplates([STANDARD_TEMPLATE, ...userTpls]);
        const def = userTpls.find(t => t.is_default);
        if (def) setActiveTemplateId(def.id);
      }
    } catch (_) {}
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ─── Kolonlar (DataTable) ────────────────────────────────────
  const baseColumns = useMemo(() => {
    const cols = [];
    const colSet = new Set(tplConfig.columns || DEFAULT_COLUMNS);
    if (colSet.has('code'))     cols.push({ id: 'code',     header: 'Stok Kodu', accessor: 'code', sortable: true, width: 110, cell: r => <span className="stock-code">{r.code}</span> });
    if (colSet.has('name'))     cols.push({ id: 'name',     header: 'Stok Adı',  accessor: 'name', sortable: true, cell: r => <span className="stock-name-cell">{r.name}{r.model ? <div style={{fontSize:11,color:'var(--text-tertiary)'}}>{r.model}</div> : null}</span> });
    if (colSet.has('barcode'))  cols.push({ id: 'barcode',  header: 'Barkod',    accessor: 'barcode', sortable: true, width: 130, cell: r => <span className="stock-secondary" style={{fontFamily:'monospace',fontSize:'0.78rem'}}>{r.barcode || '-'}</span> });
    if (colSet.has('category')) cols.push({ id: 'category', header: 'Kategori',  accessor: 'category_name', sortable: true, width: 140, cell: r => <span className="stock-secondary">{r.category_name || '-'}</span> });
    if (colSet.has('brand'))    cols.push({ id: 'brand',    header: 'Marka',     accessor: 'brand_name', sortable: true, width: 120, cell: r => <span className="stock-secondary">{r.brand_name || '-'}</span> });
    if (colSet.has('unit'))     cols.push({ id: 'unit',     header: 'Birim',     accessor: 'unit_code', sortable: true, width: 80,  cell: r => <span className="stock-secondary">{r.unit_code || '-'}</span> });
    if (colSet.has('vat'))      cols.push({ id: 'vat',      header: 'KDV',       accessor: 'vat_rate', sortable: true, width: 70, headerStyle:{textAlign:'right'}, cellStyle:{textAlign:'right'}, cell: r => <span className="stock-secondary">{fmtPercent(r.vat_rate)}</span> });
    if (tplConfig.show_warehouse_columns !== false) {
      for (const w of warehouses) {
        cols.push({
          id: 'wh_' + w.id, header: w.name, accessor: r => r.warehouse_qty?.[w.id] || 0,
          sortable: true, width: 100,
          headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
          cell: r => {
            const q = r.warehouse_qty?.[w.id] || 0;
            return <span style={{ color: q > 0 ? 'var(--text-primary)' : 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{q ? fmtQty(q) : '-'}</span>;
          },
        });
      }
    }
    if (tplConfig.show_total_column !== false) {
      cols.push({
        id: 'total', header: 'TOPLAM',
        accessor: r => r.total_qty || 0,
        sortable: true, width: 110,
        headerStyle: { textAlign: 'right', fontWeight: 700 },
        cellStyle: { textAlign: 'right' },
        cell: r => <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(r.total_qty || 0)}</strong>,
      });
    }
    return cols;
  }, [warehouses, tplConfig]);

  // Toplam satır (footer için)
  const totals = useMemo(() => {
    const t = { total: 0, byWh: {} };
    for (const s of stocks) {
      t.total += s.total_qty || 0;
      for (const w of warehouses) {
        t.byWh[w.id] = (t.byWh[w.id] || 0) + (s.warehouse_qty?.[w.id] || 0);
      }
    }
    return t;
  }, [stocks, warehouses]);

  // ─── Excel ───────────────────────────────────────────────────
  const exportExcel = () => {
    const headers = ['Kod', 'Stok Adı', 'Barkod', 'Kategori', 'Marka', 'Birim', 'KDV %'];
    for (const w of warehouses) headers.push(w.name);
    headers.push('TOPLAM');
    const rows = stocks.map(s => {
      const row = [s.code, s.name, s.barcode || '', s.category_name || '', s.brand_name || '', s.unit_code || '', s.vat_rate || 0];
      for (const w of warehouses) row.push(s.warehouse_qty?.[w.id] || 0);
      row.push(s.total_qty || 0);
      return row;
    });
    // Toplam satırı
    const totalRow = ['', 'TOPLAM', '', '', '', '', ''];
    for (const w of warehouses) totalRow.push(totals.byWh[w.id] || 0);
    totalRow.push(totals.total);
    rows.push(totalRow);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stok Listesi');
    XLSX.writeFile(wb, `stok-listesi-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // ─── Yazdır / PDF ────────────────────────────────────────────
  // PDF için tarayıcının yazdır → "PDF olarak kaydet" özelliği kullanılır (ek lib gerekmez)
  const print = () => {
    // Eğer aktif şablon banded layout (designer ile yapılmış) içeriyorsa onu kullan
    if (tplConfig.layout && Array.isArray(tplConfig.layout.elements) && tplConfig.layout.elements.length > 0) {
      const filterSummary = [
        filters.search && `Ara: ${filters.search}`,
        filters.status === 'active' ? 'Sadece aktif' : filters.status === 'passive' ? 'Sadece pasif' : 'Tümü',
        filters.category_id && categories.find(c => c.id === filters.category_id) ? `Kategori: ${categories.find(c => c.id === filters.category_id).name}` : '',
        filters.brand_id && brands.find(b => b.id === filters.brand_id) ? `Marka: ${brands.find(b => b.id === filters.brand_id).name}` : '',
        filters.warehouse_id && warehouses.find(w => w.id === filters.warehouse_id) ? `Depo: ${warehouses.find(w => w.id === filters.warehouse_id).name}` : '',
      ].filter(Boolean).join(' · ');
      const rows = stocks.map(s => ({
        id: s.id, code: s.code, name: s.name, barcode: s.barcode || '',
        category: s.category_name || '', brand: s.brand_name || '',
        unit: s.unit_code || '', vat: s.vat_rate || 0, total_qty: s.total_qty || 0,
      }));
      const html = renderBandedHtml({
        layout: tplConfig.layout,
        name: activeTemplate.name,
        mainRows: rows,
        detailRowsByMaster: {}, // ileride stok hareketleri vs. eklendiğinde doldurulur
        context: { title: tplConfig.title || 'Stok Listesi', subtitle: tplConfig.subtitle || '',
                   company_name: companyName, filter_summary: filterSummary, row_count: stocks.length, footer_text: tplConfig.footer_text || '' },
      });
      const w = window.open('', '_blank');
      if (!w) return alert('Yazdırma penceresi engellendi.');
      w.document.write(html); w.document.close();
      return;
    }
    // Fallback: eski form-based kolon yazdırma
    const html = printRef.current?.innerHTML || '';
    const today = new Date().toLocaleString('tr-TR');
    const filterSummary = tplConfig.show_filter_summary === false ? '' : [
      filters.search && `Ara: ${filters.search}`,
      filters.status === 'active' ? 'Sadece aktif' : filters.status === 'passive' ? 'Sadece pasif' : 'Tümü',
      filters.category_id && categories.find(c => c.id === filters.category_id) ? `Kategori: ${categories.find(c => c.id === filters.category_id).name}` : '',
      filters.brand_id && brands.find(b => b.id === filters.brand_id) ? `Marka: ${brands.find(b => b.id === filters.brand_id).name}` : '',
      filters.warehouse_id && warehouses.find(w => w.id === filters.warehouse_id) ? `Depo: ${warehouses.find(w => w.id === filters.warehouse_id).name}` : '',
    ].filter(Boolean).join(' · ');
    const w = window.open('', '_blank');
    if (!w) { alert('Yazdırma penceresi engellendi. Pop-up izni verin.'); return; }
    const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
    const headerBg   = tplConfig.header_color       || '#1e293b';
    const headerFg   = tplConfig.header_text_color  || '#ffffff';
    const accent     = tplConfig.accent_color       || '#3b82f6';
    const fontFamily = tplConfig.font_family        || 'Segoe UI, Arial, sans-serif';
    const baseFs     = parseInt(tplConfig.font_size) || 11;
    const orient     = tplConfig.page_orientation   || 'landscape';
    const paper      = tplConfig.paper_size         || 'A4';
    w.document.write(`<!DOCTYPE html>
<html lang="tr"><head><meta charset="utf-8"><title>${escapeHtml(tplConfig.title || 'Stok Listesi')}</title>
<style>
  @page { size: ${paper} ${orient}; margin: 12mm; }
  body { font-family: ${fontFamily}; font-size: ${baseFs}px; color: #111; margin: 0; }
  .rpt-head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid ${accent}; padding-bottom: 8px; margin-bottom: 12px; gap: 16px; }
  .rpt-head .left { display: flex; align-items: center; gap: 12px; }
  .rpt-head img.logo { max-height: 48px; max-width: 120px; object-fit: contain; }
  .rpt-head h1 { margin: 0; font-size: ${baseFs + 5}px; color: ${accent}; }
  .rpt-head .sub { font-size: ${baseFs - 1}px; color: #555; margin-top: 2px; }
  .rpt-head .subtitle { font-size: ${baseFs}px; color: #666; margin-top: 1px; font-style: italic; }
  .rpt-meta { font-size: ${baseFs - 1}px; color: #555; text-align: right; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #ddd; padding: 5px 6px; vertical-align: top; }
  th { background: ${headerBg}; color: ${headerFg}; text-align: left; font-weight: 700; font-size: ${baseFs - 1}px; letter-spacing: 0.02em; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  ${tplConfig.striped_rows !== false ? `tr:nth-child(even) td { background: #fafafa; }` : ''}
  tfoot td { font-weight: 700; background: #e5e7eb; border-top: 2px solid ${accent}; }
  .footer { margin-top: 10px; font-size: ${baseFs - 2}px; color: #888; display: flex; justify-content: space-between; }
  ${tplConfig.show_page_number !== false ? `@page { @bottom-right { content: counter(page) ' / ' counter(pages); font-size: 9px; color: #888; } }` : ''}
</style></head>
<body>
  <div class="rpt-head">
    <div class="left">
      ${tplConfig.show_logo && tplConfig.logo_url ? `<img class="logo" src="${escapeHtml(tplConfig.logo_url)}" alt="logo"/>` : ''}
      <div>
        <h1>${escapeHtml(tplConfig.title || 'Stok Listesi')}</h1>
        ${tplConfig.subtitle ? `<div class="subtitle">${escapeHtml(tplConfig.subtitle)}</div>` : ''}
        <div class="sub">${escapeHtml(companyName || '')}</div>
      </div>
    </div>
    <div class="rpt-meta">
      <div>Tarih: ${today}</div>
      ${filterSummary ? `<div>${escapeHtml(filterSummary)}</div>` : ''}
      ${tplConfig.show_row_count !== false ? `<div>Toplam Satır: ${stocks.length}</div>` : ''}
    </div>
  </div>
  ${html}
  <div class="footer">
    <div>${escapeHtml(tplConfig.footer_text || '')}</div>
    <div>${escapeHtml(activeTemplate.name)}</div>
  </div>
  <script>window.onload = function(){ window.focus(); window.print(); };</script>
</body></html>`);
    w.document.close();
  };

  // Yazdırılacak HTML — buton sütunsuz, tüm satırları içerir
  const buildPrintTable = () => {
    const cols = baseColumns.filter(c => c.id !== 'actions');
    return (
      <table>
        <thead>
          <tr>{cols.map(c => <th key={c.id} className={c.cellStyle?.textAlign === 'right' ? 'num' : ''}>{c.header}</th>)}</tr>
        </thead>
        <tbody>
          {stocks.map(s => (
            <tr key={s.id}>
              {cols.map(c => {
                const v = typeof c.accessor === 'function' ? c.accessor(s) : s[c.accessor];
                const isNum = c.cellStyle?.textAlign === 'right';
                let display = v;
                if (c.id.startsWith('wh_') || c.id === 'total') display = (v ? fmtQty(v) : '-');
                if (c.id === 'vat') display = fmtPercent(v);
                if (c.id === 'name') display = s.name + (s.model ? ' / ' + s.model : '');
                return <td key={c.id} className={isNum ? 'num' : ''}>{display ?? '-'}</td>;
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            {cols.map(c => {
              if (c.id === 'name' || c.id === 'code') return <td key={c.id}>TOPLAM</td>;
              if (c.id.startsWith('wh_')) {
                const wid = c.id.slice(3);
                return <td key={c.id} className="num">{fmtQty(totals.byWh[wid] || 0)}</td>;
              }
              if (c.id === 'total') return <td key={c.id} className="num">{fmtQty(totals.total)}</td>;
              return <td key={c.id}></td>;
            })}
          </tr>
        </tfoot>
      </table>
    );
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Stok Listesi Raporu"
        unit="stok"
        count={stocks.length}
        showFilterButton
        filtersActive={showFilterBar}
        onToggleFilters={() => setShowFilterBar(s => !s)}
        onRefresh={load}
        refreshing={loading}
        actions={
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={activeTemplateId} onChange={e => setActiveTemplateId(e.target.value)}
              title="Rapor şablonu"
              style={{ padding: '5px 8px', border: '1px solid var(--border-secondary)', borderRadius: 'var(--r-md)', fontSize: 'var(--text-sm)', background: 'var(--bg-primary)' }}>
              {templates.map(t => <option key={t.id} value={t.id}>{t.is_default ? '★ ' : ''}{t.name}</option>)}
            </select>
            <Button size="sm" variant="ghost" onClick={() => setShowTemplateMgr(true)} title="Şablonları yönet">⚙ Şablon</Button>
            <Button size="sm" variant="ghost" onClick={exportExcel} disabled={!stocks.length}>📊 Excel</Button>
            <Button size="sm" variant="ghost" onClick={print}      disabled={!stocks.length}>🖨 Yazdır / PDF</Button>
            {onNavigate && <Button size="sm" variant="ghost" onClick={() => onNavigate('reports-stocks')}>← Geri</Button>}
          </span>
        }
      />

      <FilterBar
        active={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'search',       type: 'text',   label: 'Ara (kod / ad / barkod / model)', flex: '2 1 240px' },
          { key: 'status',       type: 'select', label: 'Durum', options: [
            { value: 'active', label: 'Sadece Aktif' },
            { value: 'passive', label: 'Sadece Pasif' },
            { value: 'all',    label: 'Tümü' },
          ]},
          { key: 'category_id',  type: 'select', label: 'Kategori', options: [
            { value: '', label: 'Tümü' },
            ...categories.map(c => ({ value: c.id, label: c.name })),
          ]},
          { key: 'brand_id',     type: 'select', label: 'Marka', options: [
            { value: '', label: 'Tümü' },
            ...brands.map(b => ({ value: b.id, label: b.name })),
          ]},
          { key: 'warehouse_id', type: 'select', label: 'Depo', options: [
            { value: '', label: 'Tüm depolar' },
            ...warehouses.map(w => ({ value: w.id, label: w.name })),
          ]},
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="report-stock-list-v1"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Filtrelere uygun stok yok"
          data={stocks}
          rowKey="id"
          columns={baseColumns}
        />
      </div>

      {/* Yazdırma için gizli HTML — print fonksiyonunda yeni pencereye taşınır */}
      <div ref={printRef} style={{ display: 'none' }}>{buildPrintTable()}</div>

      {/* Şablon Yöneticisi modal'ı (liste + designer launcher) */}
      {showTemplateMgr && (
        <TemplateManager
          templates={templates}
          activeId={activeTemplateId}
          onClose={() => setShowTemplateMgr(false)}
          onChanged={loadTemplates}
          onSelectTemplate={(id) => setActiveTemplateId(id)}
          onNavigateToDesigner={() => onNavigate && onNavigate('report-designer')}
        />
      )}
    </div>
  );
}

// ─── Şablon Yöneticisi (liste) ───────────────────────────────────
// "Tasarla" tıklayınca tam-ekran banded designer sayfasına yönlendirir.
function TemplateManager({ templates, activeId, onClose, onChanged, onSelectTemplate, onNavigateToDesigner }) {
  const userTemplates = templates.filter(t => t.id !== '__default__');

  const remove = async (t) => {
    if (!window.confirm(`"${t.name}" şablonu silinsin mi?`)) return;
    try { await api.delete('/reports/templates/' + t.id); onChanged && onChanged(); }
    catch (e) { alert(e.response?.data?.message || 'Hata'); }
  };

  const setDefault = async (t) => {
    try {
      await api.put('/reports/templates/' + t.id, { name: t.name, is_default: true, config: t.config });
      onChanged && onChanged();
    } catch (e) { alert(e.response?.data?.message || 'Hata'); }
  };

  const openDesigner = (id) => {
    try { localStorage.setItem('report_designer_id', id || 'new'); } catch (_) {}
    onClose && onClose();
    onNavigateToDesigner && onNavigateToDesigner();
  };

  return (
    <Modal title="Rapor Şablonları" size="md" onClose={onClose}
      cancelAction={{ label: 'Kapat', onClick: onClose }}
      primaryAction={{
        label: '+ Yeni Şablon (Tasarımla)',
        onClick: () => openDesigner('new'),
      }}>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 10 }}>
        Şablon tasarımcısı: kağıt üzerinde Page Header / Detail / Page Footer band'ları, sürükle-bırak metin/alan/logo/çizgi/kutu yerleştirme. Detail band'ı her veri satırı için tekrar eder.
      </div>

      <h4 style={{ margin: '6px 0', fontSize: '0.95rem' }}>Mevcut Şablonlar</h4>
      {userTemplates.length === 0 ? (
        <div style={{ padding: 14, background: 'var(--bg-secondary)', borderRadius: 6, color: 'var(--text-tertiary)', fontSize: 12 }}>
          Henüz özel şablon yok. Üstteki "+ Yeni Şablon (Tasarımla)" butonuna tıklayarak başlayın.
        </div>
      ) : (
        <table className="stocks-table" style={{ marginTop: 6 }}>
          <thead>
            <tr>
              <th>Ad</th>
              <th style={{ width: 80, textAlign: 'center' }}>Vars.</th>
              <th style={{ width: 220 }}></th>
            </tr>
          </thead>
          <tbody>
            {userTemplates.map(t => (
              <tr key={t.id} style={t.id === activeId ? { background: 'rgba(59,130,246,0.08)' } : undefined}>
                <td>
                  <strong>{t.name}</strong>
                  {t.id === activeId && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--accent-primary)' }}>● Aktif</span>}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {t.is_default ? '★' : <Button size="sm" variant="ghost" onClick={() => setDefault(t)}>Vars. yap</Button>}
                </td>
                <td>
                  <Button size="sm" variant="ghost" onClick={() => openDesigner(t.id)}>✏ Tasarla</Button>{' '}
                  <Button size="sm" variant="ghost" onClick={() => remove(t)}>🗑</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

// ─── Şablon Tasarımcısı (FastReport benzeri) ─────────────────────
// Sol: alanlar (başlık, logo, renkler, kolonlar, sayfa düzeni). Sağ: canlı önizleme.
function TemplateDesignerModal({ initial, onClose, onSaved }) {
  const isNew = !!initial._new;
  const [name, setName] = useState(initial.name || '');
  const [config, setConfig] = useState(mergeConfig(initial.config));
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  const toggleColumn = (k) => {
    const cur = config.columns || DEFAULT_COLUMNS;
    const next = cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k];
    set('columns', next);
  };
  const moveColumn = (k, dir) => {
    const cur = [...(config.columns || DEFAULT_COLUMNS)];
    const i = cur.indexOf(k);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= cur.length) return;
    [cur[i], cur[j]] = [cur[j], cur[i]];
    set('columns', cur);
  };

  const save = async () => {
    if (!name.trim()) { alert('Şablon adı gerekli'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/reports/templates', {
          report_key: 'stock_list', name: name.trim(),
          is_default: false, config,
        });
      } else {
        await api.put('/reports/templates/' + initial.id, { name: name.trim(), is_default: !!initial.is_default, config });
      }
      onSaved && onSaved(initial.id || null);
    } catch (e) {
      alert(e.response?.data?.message || 'Hata');
    } finally { setSaving(false); }
  };

  // Sadece düzenleyicideki kolonların önizlemede görünmesi için sıralı liste
  const orderedCols = (config.columns || DEFAULT_COLUMNS).filter(k => ALL_COLUMNS.find(c => c.key === k));

  return (
    <Modal title={isNew ? 'Yeni Şablon Tasarımı' : `Şablonu Düzenle · ${initial.name}`}
      size="xl" onClose={onClose}
      cancelAction={{ label: 'Vazgeç', onClick: onClose }}
      primaryAction={{ label: saving ? 'Kaydediliyor…' : 'Kaydet', onClick: save, disabled: saving || !name.trim() }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 360px) 1fr', gap: 16 }}>
        {/* SOL: tasarımcı alanları */}
        <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
          <Field label="Şablon Adı *">
            <input type="text" className="ui-input" value={name} onChange={e => setName(e.target.value)} placeholder="Örn: Standart Beyaz" />
          </Field>

          <Section title="Başlık & Logo">
            <Field label="Rapor Başlığı"><input type="text" className="ui-input" value={config.title} onChange={e => set('title', e.target.value)} /></Field>
            <Field label="Alt Başlık"><input type="text" className="ui-input" value={config.subtitle} onChange={e => set('subtitle', e.target.value)} placeholder="(opsiyonel)" /></Field>
            <Checkbox checked={config.show_logo} onChange={v => set('show_logo', v)}>Logo göster</Checkbox>
            {config.show_logo && (
              <Field label="Logo URL"><input type="text" className="ui-input" value={config.logo_url} onChange={e => set('logo_url', e.target.value)} placeholder="https://..." /></Field>
            )}
          </Section>

          <Section title="Renk & Yazı Tipi">
            <ColorRow label="Vurgu Rengi"     value={config.accent_color}      onChange={v => set('accent_color', v)} />
            <ColorRow label="Başlık Arka Plan" value={config.header_color}      onChange={v => set('header_color', v)} />
            <ColorRow label="Başlık Yazı"      value={config.header_text_color} onChange={v => set('header_text_color', v)} />
            <Field label="Yazı Tipi">
              <select className="ui-input" value={config.font_family} onChange={e => set('font_family', e.target.value)}>
                <option value="Segoe UI, Arial, sans-serif">Segoe UI</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="Helvetica, Arial, sans-serif">Helvetica</option>
                <option value="Calibri, sans-serif">Calibri</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="'Courier New', monospace">Courier New</option>
              </select>
            </Field>
            <Field label={`Yazı Boyutu (${config.font_size}px)`}>
              <input type="range" min="8" max="16" value={config.font_size} onChange={e => set('font_size', parseInt(e.target.value))} style={{ width: '100%' }} />
            </Field>
            <Checkbox checked={config.striped_rows} onChange={v => set('striped_rows', v)}>Şeritli satırlar (zebra)</Checkbox>
          </Section>

          <Section title="Sayfa Düzeni">
            <Field label="Kağıt Boyutu">
              <select className="ui-input" value={config.paper_size} onChange={e => set('paper_size', e.target.value)}>
                <option value="A4">A4</option><option value="A3">A3</option><option value="Letter">Letter</option><option value="Legal">Legal</option>
              </select>
            </Field>
            <Field label="Sayfa Yönü">
              <select className="ui-input" value={config.page_orientation} onChange={e => set('page_orientation', e.target.value)}>
                <option value="landscape">Yatay</option><option value="portrait">Dikey</option>
              </select>
            </Field>
          </Section>

          <Section title="Kolonlar (görünür ve sıralı)">
            <div style={{ display: 'grid', gap: 4 }}>
              {(config.columns || DEFAULT_COLUMNS).map((k) => {
                const meta = ALL_COLUMNS.find(c => c.key === k);
                if (!meta) return null;
                return (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', background: 'var(--bg-secondary)', borderRadius: 4 }}>
                    <input type="checkbox" checked onChange={() => toggleColumn(k)} />
                    <span style={{ flex: 1, fontSize: 13 }}>{meta.label}</span>
                    <Button size="sm" variant="ghost" onClick={() => moveColumn(k, -1)} title="Yukarı">↑</Button>
                    <Button size="sm" variant="ghost" onClick={() => moveColumn(k, +1)} title="Aşağı">↓</Button>
                  </div>
                );
              })}
              {ALL_COLUMNS.filter(c => !(config.columns || []).includes(c.key)).map(c => (
                <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', opacity: 0.6 }}>
                  <input type="checkbox" checked={false} onChange={() => toggleColumn(c.key)} />
                  <span style={{ flex: 1, fontSize: 13 }}>{c.label}</span>
                </div>
              ))}
            </div>
            <Checkbox checked={config.show_warehouse_columns} onChange={v => set('show_warehouse_columns', v)}>Depo kolonlarını göster</Checkbox>
            <Checkbox checked={config.show_total_column}      onChange={v => set('show_total_column', v)}>Toplam kolonunu göster</Checkbox>
          </Section>

          <Section title="Üst / Alt Bilgi">
            <Checkbox checked={config.show_filter_summary} onChange={v => set('show_filter_summary', v)}>Filtre özetini göster</Checkbox>
            <Checkbox checked={config.show_row_count}      onChange={v => set('show_row_count', v)}>Toplam satır sayısını göster</Checkbox>
            <Checkbox checked={config.show_page_number}    onChange={v => set('show_page_number', v)}>Sayfa numarasını göster</Checkbox>
            <Field label="Alt Bilgi Metni"><input type="text" className="ui-input" value={config.footer_text} onChange={e => set('footer_text', e.target.value)} placeholder="Örn: Gizli — sadece iç kullanım" /></Field>
          </Section>
        </div>

        {/* SAĞ: canlı önizleme */}
        <div style={{ background: '#f1f5f9', padding: 14, borderRadius: 8, overflow: 'auto', maxHeight: '70vh' }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Önizleme · {config.paper_size} {config.page_orientation === 'landscape' ? 'Yatay' : 'Dikey'}
          </div>
          <PreviewSheet config={config} orderedCols={orderedCols} />
        </div>
      </div>
    </Modal>
  );
}

// ─── Tasarım önizlemesi (yazdırma stilini taklit eder) ───────────
function PreviewSheet({ config, orderedCols }) {
  const accent     = config.accent_color || '#3b82f6';
  const headerBg   = config.header_color || '#1e293b';
  const headerFg   = config.header_text_color || '#fff';
  const fontFamily = config.font_family || 'Segoe UI, Arial, sans-serif';
  const baseFs     = parseInt(config.font_size) || 11;
  const sampleStocks = [
    { code: 'STK001', name: 'Örnek Ürün A', barcode: '8690000000017', category: 'Genel', brand: 'Marka X', unit: 'Adet', vat: 20, wh1: 25, wh2: 10, total: 35 },
    { code: 'STK002', name: 'Örnek Ürün B', barcode: '8690000000024', category: 'Hırdavat', brand: 'Marka Y', unit: 'Kg',   vat: 10, wh1: 0,  wh2: 40, total: 40 },
    { code: 'STK003', name: 'Örnek Ürün C', barcode: '8690000000031', category: 'Tekstil',  brand: 'Marka Z', unit: 'Mt',   vat: 1,  wh1: 12, wh2: 8,  total: 20 },
  ];
  const fmt = (n) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div style={{ background: '#fff', padding: 18, borderRadius: 4, boxShadow: '0 2px 6px rgba(0,0,0,0.08)', fontFamily, fontSize: baseFs }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: `3px solid ${accent}`, paddingBottom: 8, marginBottom: 12, gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {config.show_logo && config.logo_url
            ? <img src={config.logo_url} alt="logo" style={{ maxHeight: 42, maxWidth: 110, objectFit: 'contain' }}/>
            : (config.show_logo ? <div style={{ width: 48, height: 42, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#999' }}>LOGO</div> : null)}
          <div>
            <h1 style={{ margin: 0, fontSize: baseFs + 5, color: accent }}>{config.title || 'Stok Listesi'}</h1>
            {config.subtitle && <div style={{ fontSize: baseFs, color: '#666', fontStyle: 'italic' }}>{config.subtitle}</div>}
            <div style={{ fontSize: baseFs - 1, color: '#555' }}>Firma Adı (örnek)</div>
          </div>
        </div>
        <div style={{ fontSize: baseFs - 1, color: '#555', textAlign: 'right' }}>
          <div>Tarih: {new Date().toLocaleDateString('tr-TR')}</div>
          {config.show_filter_summary && <div>Sadece aktif</div>}
          {config.show_row_count && <div>Toplam Satır: 3</div>}
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: baseFs }}>
        <thead>
          <tr>
            {orderedCols.includes('code')     && <th style={thStyle(headerBg, headerFg, baseFs)}>Stok Kodu</th>}
            {orderedCols.includes('name')     && <th style={thStyle(headerBg, headerFg, baseFs)}>Stok Adı</th>}
            {orderedCols.includes('barcode')  && <th style={thStyle(headerBg, headerFg, baseFs)}>Barkod</th>}
            {orderedCols.includes('category') && <th style={thStyle(headerBg, headerFg, baseFs)}>Kategori</th>}
            {orderedCols.includes('brand')    && <th style={thStyle(headerBg, headerFg, baseFs)}>Marka</th>}
            {orderedCols.includes('unit')     && <th style={thStyle(headerBg, headerFg, baseFs)}>Birim</th>}
            {orderedCols.includes('vat')      && <th style={thStyle(headerBg, headerFg, baseFs, 'right')}>KDV</th>}
            {config.show_warehouse_columns && <th style={thStyle(headerBg, headerFg, baseFs, 'right')}>Ana Depo</th>}
            {config.show_warehouse_columns && <th style={thStyle(headerBg, headerFg, baseFs, 'right')}>Şube</th>}
            {config.show_total_column && <th style={thStyle(headerBg, headerFg, baseFs, 'right')}>TOPLAM</th>}
          </tr>
        </thead>
        <tbody>
          {sampleStocks.map((s, i) => (
            <tr key={i} style={config.striped_rows && i % 2 === 1 ? { background: '#fafafa' } : undefined}>
              {orderedCols.includes('code')     && <td style={tdStyle()}>{s.code}</td>}
              {orderedCols.includes('name')     && <td style={tdStyle()}>{s.name}</td>}
              {orderedCols.includes('barcode')  && <td style={tdStyle()}>{s.barcode}</td>}
              {orderedCols.includes('category') && <td style={tdStyle()}>{s.category}</td>}
              {orderedCols.includes('brand')    && <td style={tdStyle()}>{s.brand}</td>}
              {orderedCols.includes('unit')     && <td style={tdStyle()}>{s.unit}</td>}
              {orderedCols.includes('vat')      && <td style={tdStyle('right')}>{s.vat}%</td>}
              {config.show_warehouse_columns && <td style={tdStyle('right')}>{s.wh1 ? fmt(s.wh1) : '-'}</td>}
              {config.show_warehouse_columns && <td style={tdStyle('right')}>{s.wh2 ? fmt(s.wh2) : '-'}</td>}
              {config.show_total_column      && <td style={{ ...tdStyle('right'), fontWeight: 700 }}>{fmt(s.total)}</td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={orderedCols.length} style={{ ...tdStyle(), background: '#e5e7eb', fontWeight: 700, borderTop: `2px solid ${accent}` }}>TOPLAM</td>
            {config.show_warehouse_columns && <td style={{ ...tdStyle('right'), background: '#e5e7eb', fontWeight: 700, borderTop: `2px solid ${accent}` }}>37,00</td>}
            {config.show_warehouse_columns && <td style={{ ...tdStyle('right'), background: '#e5e7eb', fontWeight: 700, borderTop: `2px solid ${accent}` }}>58,00</td>}
            {config.show_total_column &&      <td style={{ ...tdStyle('right'), background: '#e5e7eb', fontWeight: 700, borderTop: `2px solid ${accent}` }}>95,00</td>}
          </tr>
        </tfoot>
      </table>

      {(config.footer_text || config.show_page_number) && (
        <div style={{ marginTop: 10, fontSize: baseFs - 2, color: '#888', display: 'flex', justifyContent: 'space-between' }}>
          <div>{config.footer_text || ''}</div>
          {config.show_page_number && <div>1 / 1</div>}
        </div>
      )}
    </div>
  );
}

const thStyle = (bg, fg, fs, align) => ({
  background: bg, color: fg, padding: '5px 6px', border: '1px solid #ddd',
  textAlign: align || 'left', fontWeight: 700, fontSize: fs - 1, letterSpacing: '0.02em',
});
const tdStyle = (align) => ({
  padding: '5px 6px', border: '1px solid #ddd', textAlign: align || 'left',
  fontVariantNumeric: align === 'right' ? 'tabular-nums' : 'normal',
});

// ─── Yardımcı küçük komponentler ─────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ borderTop: '1px solid var(--border-secondary)', paddingTop: 10, display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label style={{ display: 'grid', gap: 3 }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
      {children}
    </label>
  );
}
function Checkbox({ checked, onChange, children }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
      <span>{children}</span>
    </label>
  );
}
function ColorRow({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
      <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)} style={{ width: 36, height: 28, padding: 0, border: '1px solid var(--border-secondary)', borderRadius: 4, cursor: 'pointer' }} />
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} className="ui-input" style={{ width: 90, fontFamily: 'monospace', fontSize: 12 }} />
    </div>
  );
}
